/**
 * The Strategies list grouped by product (CU-869fm5juc, plan D10-D11).
 *
 * A strategy only means something inside its product: the product fixes
 * the sites and the rental-length brackets, and the strategies of one
 * product compete for each price by Prio. So the list is products, each
 * with its strategies underneath, and Run now lives on the product.
 *
 * Pure: no React. The table runs in mui-datatables' `serverSide` mode —
 * it neither searches, filters, sorts nor pages — and hands this module
 * the search text, the column filters (with the same `logic` functions
 * the columns declare), the sort and the page. This is the one place the
 * group semantics live:
 *
 *   - groups follow the order of Setup → Products (the catalogue), so a
 *     product sits in the same place on both screens; strategies whose
 *     product is not in the catalogue (none, or gone) form a last group,
 *     "No product" (Curro, 2026-10-23);
 *   - inside a group: Prio, then name; sorting by a column sorts inside
 *     each product, Prio breaking ties;
 *   - search finds strategies or products — a product whose name matches
 *     comes whole;
 *   - filters hide strategies; a product left without any is hidden;
 *   - a product without strategies is listed only with no search and no
 *     column filter, or when the search matches its name. The Enabled /
 *     Disabled / All toggle is not such a filter (`narrows: false`): it
 *     defaults to Enabled, and a product with no strategy has none to
 *     hide — it stays listed so it can get its first one (criterion 2);
 *   - pages are products: a product is never split between two pages;
 *   - a product is collapsed by default — its header says how many
 *     strategies it holds — and opens on its chevron (Curro, 2026-10-26).
 *     `expanded` is the set of group keys the user opened. While a search
 *     or a column filter is on, every product with a match opens, so a
 *     match is never hidden behind a chevron.
 */

const same = (a, b) => a != null && b != null && String(a) === String(b)
const norm = (s) => String(s ?? '').toLowerCase()

/** The key of a group: the product's id, or `no-product` for the last group. */
export const groupKey = (product) => (product ? String(product.id) : 'no-product')

/** What a header's chevron says while a search or column filter holds its product open (QA re-check). */
export const LOCKED_OPEN = 'Matches of your search stay open — clear it to collapse'

/**
 * The expanded set after the user clicks a header's chevron. A product
 * held open by a search or a column filter (`locked`) ignores the click:
 * the set is returned unchanged, so clearing the search shows the product
 * as the user left it.
 */
export function toggleExpanded(expanded, key, { locked = false } = {}) {
	if (locked) return expanded instanceof Set ? expanded : new Set(expanded || [])
	const next = new Set(expanded || [])
	if (next.has(key)) next.delete(key)
	else next.add(key)
	return next
}

/** A row hidden by any active filter. `filters` = `[{ value(row), logic(value, values), values }]`; `logic` returns true to hide (mui-datatables' contract). */
const hiddenByFilters = (row, filters) => filters.some(f => f.values?.length && f.logic(f.value(row), f.values, row))

const activeFilters = (filters) => (filters || []).filter(f => f?.values?.length)

const byPrioThenName = (a, b) => ((a.priority ?? Infinity) - (b.priority ?? Infinity))
	|| norm(a.name).localeCompare(norm(b.name))

function compareBy(sort) {
	if (!sort?.value) return byPrioThenName
	const dir = sort.direction === 'desc' ? -1 : 1
	return (a, b) => {
		const va = sort.value(a), vb = sort.value(b)
		let c = 0
		if (va == null && vb != null) c = 1
		else if (va != null && vb == null) c = -1
		else if (typeof va === 'number' && typeof vb === 'number') c = va - vb
		else c = norm(va).localeCompare(norm(vb), undefined, { numeric: true })
		return (c * dir) || byPrioThenName(a, b)
	}
}

/**
 * `{ groups, productCount, strategyCount, pageCount }`.
 *   rows      — the table's strategy rows (`productId`, `name`, `priority`, …)
 *   products  — the catalogue, in Setup order (`{ id, name, rows }`)
 *   search    — the search box's text
 *   searchOf  — row → the text a search looks in (default: the name)
 *   filters   — `[{ value(row), logic, values, narrows? }]`
 *   sort      — `{ value(row), direction: 'asc' | 'desc' }` or null
 *   page, perPage — products per page; `perPage` falsy = one page
 *   expanded  — the keys (`groupKey`) the user opened
 * A group is `{ key, product | null, strategies, all, expanded, locked }`
 * (`locked`: held open by a search or column filter, the chevron is off): `strategies` are the
 * ones shown, `all` every strategy of the product (the header's counts
 * and its Run now judge the product, not the filtered view).
 * `productCount` counts the groups that are products, `strategyCount`
 * the strategies shown, both over every page.
 */
export function groupByProduct(rows, products, { search = '', searchOf = r => r.name, filters = [], sort = null, page = 0, perPage = 0, expanded = null } = {}) {
	const list = Array.isArray(rows) ? rows : []
	const catalogue = Array.isArray(products) ? products : []
	const q = norm(search).trim()
	const active = activeFilters(filters)
	const columnFilters = active.filter(f => f.narrows !== false).length > 0
	const narrowed = !!q || columnFilters
	const cmp = compareBy(sort)

	const known = new Set(catalogue.map(p => String(p.id)))
	const buckets = catalogue.map(p => ({ product: p, all: list.filter(r => same(r.productId, p.id)) }))
	const orphans = list.filter(r => r.productId == null || !known.has(String(r.productId)))
	if (orphans.length) buckets.push({ product: null, all: orphans })

	const groups = []
	for (const b of buckets) {
		const productHit = !!q && b.product != null && norm(b.product.name).includes(q)
		const shown = b.all
			.filter(r => !hiddenByFilters(r, active))
			.filter(r => !q || productHit || norm(searchOf(r)).includes(q))
			.sort(cmp)
		const key = groupKey(b.product)
		const opened = !!expanded?.has?.(key)
		if (shown.length) {
			groups.push({ key, product: b.product, strategies: shown, all: [...b.all].sort(byPrioThenName), expanded: opened || narrowed, locked: narrowed })
		} else if (b.product && !b.all.length && (!narrowed || (productHit && !columnFilters))) {
			groups.push({ key, product: b.product, strategies: [], all: [], expanded: opened })
		}
	}

	const productCount = groups.filter(g => g.product).length
	const strategyCount = groups.reduce((n, g) => n + g.strategies.length, 0)
	const size = perPage > 0 ? perPage : Math.max(groups.length, 1)
	const pageCount = Math.max(1, Math.ceil(groups.length / size))
	const at = Math.min(Math.max(0, page), pageCount - 1)
	return { groups: groups.slice(at * size, at * size + size), productCount, strategyCount, groupCount: groups.length, pageCount, page: at }
}

/**
 * The table's rows for one page: a `product` row per group and, when the
 * group is expanded, its strategies (`kind: 'strategy'`, with `stripe`:
 * their 0-based place inside the group, for the zebra) or an `empty` row
 * for a product without strategies. A collapsed group is its header only.
 */
export function flatten(groups) {
	const out = []
	for (const g of groups || []) {
		const key = g.key ?? groupKey(g.product)
		const expanded = g.expanded !== false
		out.push({ kind: 'product', id: `product:${key}`, key, product: g.product, strategies: g.all, shown: g.strategies.length, expanded, locked: !!g.locked })
		if (!expanded) continue
		if (!g.strategies.length) out.push({ kind: 'empty', id: `empty:${key}`, product: g.product })
		g.strategies.forEach((s, i) => out.push({ ...s, kind: 'strategy', stripe: i }))
	}
	return out
}

const n = (k, one, many) => `${k} ${k === 1 ? one : many}`

/** "3 strategies · 2 on" / "no strategy" — what a collapsed header holds. */
export function strategyCountLabel(strategies) {
	const list = Array.isArray(strategies) ? strategies : []
	if (!list.length) return 'no strategy'
	return `${n(list.length, 'strategy', 'strategies')} · ${list.filter(s => s.enabled).length} on`
}

/** "5 rows · 5 sites · 3 strategies · 2 on" / "2 rows · 2 sites · no strategy" — the header's line. */
export function productMeta(product, strategies) {
	const rows = product?.rows || []
	const sites = new Set(rows.map(r => r.siteId).filter(v => v != null).map(String)).size
	return `${n(rows.length, 'row', 'rows')} · ${n(sites, 'site', 'sites')} · ${strategyCountLabel(strategies)}`
}

/** "N products · M strategies" — the footer. */
export const footerLabel = (productCount, strategyCount) => `${n(productCount, 'product', 'products')} · ${n(strategyCount, 'strategy', 'strategies')}`
